package com.schoolconnect.app.feature.parents

import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.NetworkActivityTracker
import com.schoolconnect.app.datasource.network.request.ParentWrapperData
import com.schoolconnect.app.datasource.network.request.ParentUpdateRequest
import com.schoolconnect.app.datasource.network.request.StudentParentWrapperCreateRequest
import com.schoolconnect.app.datasource.network.request.StudentParentLinkUpdateRequest
import com.schoolconnect.app.datasource.network.request.ParentFilterRequest
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.SchoolStatisticsResponse
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.feature.common.resolveServerPageLoadState
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.repository.analytics.SchoolStatisticsRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.parent.ParentRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ParentsViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] ParentsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val reloadDebouncer = DebouncedActionRunner(scope)
    private val studentPickerDebouncer = DebouncedActionRunner(scope)
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private var analyticsJob: Job? = null
    private var parentsJob: Job? = null
    private var bootstrapJob: Job? = null
    private var studentPickerJob: Job? = null
    private var classSectionLookupJob: Job? = null
    private val normalizedSchoolId = UserSessionStore.getHomeRoleSchoolId().trim()
    private val cachedStatistics = SchoolStatisticsRepository.peekSchoolStatistics(normalizedSchoolId)
    private val _uiState = MutableStateFlow(
        ParentsUiState(
            isLoading = false,
            isPageLoading = normalizedSchoolId.isNotBlank(),
            isAnalyticsLoading = normalizedSchoolId.isNotBlank(),
            analytics = cachedStatistics
        )
    )

    val uiState: StateFlow<ParentsUiState> = _uiState.asStateFlow()

    init {
        if (normalizedSchoolId.isBlank()) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    isPageLoading = false,
                    isAnalyticsLoading = false,
                    errorMessage = "School context was not available for the parents list."
                )
            }
        } else {
            refreshParentsData(forceRemote = false)
            loadParentClassSectionFilters(forceRemote = false)
        }
    }

    private fun loadParentClassSectionFilters(forceRemote: Boolean) {
        classSectionLookupJob?.cancel()
        classSectionLookupJob = scope.launch {
            _uiState.update { it.copy(isFilterLookupLoading = true, filterLookupError = "") }
            ClassSectionRepository.loadSchoolClassSectionLookup(
                schoolId = normalizedSchoolId,
                forceRemote = forceRemote
            ).fold(
                onSuccess = { rows ->
                    val options = rows.mapNotNull { row ->
                        val id = row.id?.trim().orEmpty()
                        val className = row.className?.trim().orEmpty()
                        val sectionName = row.sectionName?.trim().orEmpty()
                        if (id.isBlank() || className.isBlank() || sectionName.isBlank()) null
                        else ParentFilterClassSectionOption(id, className, sectionName)
                    }.distinctBy { it.id }
                        .sortedWith(
                            compareBySmartText<ParentFilterClassSectionOption> { it.className }
                                .thenBy { it.sectionName }
                        )
                    val classes = options.map { it.className }.distinct().sortedWith(compareBySmartText { it })
                    val sectionsByClass = options.groupBy({ it.className }, { it.sectionName })
                        .mapValues { (_, sections) -> sections.distinct().sortedWith(compareBySmartText { it }) }
                    val sections = options.map { it.sectionName }.distinct().sortedWith(compareBySmartText { it })
                    _uiState.update {
                        it.copy(
                            filterClassSectionOptions = options,
                            filterClassOptions = classes,
                            filterSectionOptions = sections,
                            filterSectionsByClass = sectionsByClass,
                            isFilterLookupLoading = false,
                            filterLookupError = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isFilterLookupLoading = false,
                            filterLookupError = throwable.message.orEmpty().ifBlank { "Unable to load class and section filters." }
                        )
                    }
                }
            )
        }
    }

    fun onEvent(event: ParentsUiEvent) {
        when (event) {
            ParentsUiEvent.BackClicked -> Unit
            ParentsUiEvent.RefreshRequested -> refreshParentsData(forceRemote = true)
            is ParentsUiEvent.SearchChanged -> {
                _uiState.update {
                    it.copy(
                        searchQuery = event.query,
                        pageIndex = 0,
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                scheduleParentsReload()
            }
            is ParentsUiEvent.FiltersApplied -> {
                _uiState.update {
                    it.copy(
                        filters = event.filters,
                        pageIndex = 0,
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                refreshParentsPage(forceRemote = true)
            }
            ParentsUiEvent.FiltersReset -> {
                _uiState.update {
                    it.copy(
                        filters = ParentFilterCriteria(),
                        searchQuery = "",
                        pageIndex = 0,
                        errorMessage = "",
                        infoMessage = "Filters cleared"
                    )
                }
                refreshParentsPage(forceRemote = true)
            }
            is ParentsUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.isSortAscending else true
                    it.copy(sortField = event.field, isSortAscending = nextAscending, pageIndex = 0)
                }
                refreshParentsPage(forceRemote = true)
            }
            is ParentsUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                refreshParentsPage(forceRemote = false)
            }
            is ParentsUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(pageSize = event.pageSize.coerceAtLeast(1), pageIndex = 0) }
                refreshParentsPage(forceRemote = false)
            }
            ParentsUiEvent.StudentPickerOpened -> {
                if (_uiState.value.studentOptions.isEmpty() && !_uiState.value.studentPickerIsLoading) {
                    loadStudentPickerPage(reset = true, forceRemote = false)
                }
            }
            is ParentsUiEvent.StudentPickerSearchChanged -> {
                _uiState.update {
                    it.copy(
                        studentPickerQuery = event.query,
                        studentPickerPageIndex = 0,
                        studentPickerTotalCount = 0,
                        studentPickerHasMore = true,
                        studentPickerError = "",
                        studentOptions = emptyList()
                    )
                }
                studentPickerDebouncer.submit(immediate = false) {
                    loadStudentPickerPage(reset = true, forceRemote = false)
                }
            }
            ParentsUiEvent.StudentPickerLoadMore -> {
                val state = _uiState.value
                if (state.studentPickerHasMore && !state.studentPickerIsLoading && !state.studentPickerIsLoadingMore) {
                    loadStudentPickerPage(reset = false, forceRemote = false)
                }
            }
            is ParentsUiEvent.SaveParent -> saveParent(event.draft)
            ParentsUiEvent.ParentEditorDismissed -> _uiState.update {
                it.copy(isLoading = false, isSaving = false, apiErrorMessage = "")
            }
            ParentsUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", apiErrorMessage = "", infoMessage = "")
            }
        }
    }

    private fun refreshParentsData(forceRemote: Boolean) {
        _uiState.update {
            it.copy(
                isAnalyticsLoading = true,
                isLoading = true,
                isPageLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }
        bootstrapSequencer.startBootstrap(
            forceRemote = forceRemote,
            onAnalytics = { loadParentAnalytics(forceRemote) },
            onPage = { pageForceRemote -> loadParentPage(pageForceRemote) }
        )
    }

    private fun saveParent(draft: ParentEditorDraft) {
        if (_uiState.value.isSaving) return
        if (draft.id.isNotBlank()) {
            val validationError = validateEditDraft(draft)
            if (validationError != null) {
                _uiState.update { it.copy(errorMessage = validationError, apiErrorMessage = "", infoMessage = "") }
                return
            }
            _uiState.update { it.copy(isLoading = true, isSaving = true, errorMessage = "", apiErrorMessage = "", infoMessage = "") }
            scope.launch {
                ParentRepository.updateParent(
                    parentId = draft.id,
                    schoolId = normalizedSchoolId,
                    request = ParentUpdateRequest(
                        name = draft.primaryContactName.trim(),
                        mobile = draft.primaryContactMobile.trim(),
                        email = draft.primaryContactEmail.trim().ifBlank { null },
                        customAttributes = mapOf(
                            "relationship_type" to draft.primaryRelationshipCode.trim()
                        ),
                        isActive = draft.statusCode == SchoolKeys.V_STATUS_ACTIVE
                    ),
                    currentUserId = UserSessionStore.getUserId().trim().ifBlank { null }
                ).fold(
                    onSuccess = {
                        scope.launch {
                            val secondaryResult = if (draft.secondaryParentId.isNotBlank()) {
                                ParentRepository.updateParent(
                                    parentId = draft.secondaryParentId,
                                    schoolId = normalizedSchoolId,
                                    request = ParentUpdateRequest(
                                        name = draft.secondaryContactName.trim(),
                                        mobile = draft.secondaryContactMobile.trim(),
                                        email = draft.secondaryContactEmail.trim().ifBlank { null },
                                        customAttributes = mapOf(
                                            "relationship_type" to draft.secondaryRelationshipCode.trim()
                                        ),
                                        isActive = draft.statusCode == SchoolKeys.V_STATUS_ACTIVE
                                    ),
                                    currentUserId = UserSessionStore.getUserId().trim().ifBlank { null }
                                )
                            } else if (draft.secondaryContactName.isNotBlank()) {
                                ParentRepository.createAdditionalParentAndLink(
                                    schoolId = normalizedSchoolId,
                                    studentId = draft.selectedStudentId,
                                    name = draft.secondaryContactName,
                                    mobile = draft.secondaryContactMobile,
                                    email = draft.secondaryContactEmail,
                                    relationshipType = draft.secondaryRelationshipCode.toApiRelationshipType(),
                                    isActive = draft.statusCode == SchoolKeys.V_STATUS_ACTIVE,
                                    currentUserId = UserSessionStore.getUserId().trim().ifBlank { null }
                                )
                            } else Result.success(Unit)
                            secondaryResult.getOrElse { throwable ->
                                _uiState.update { it.copy(isLoading = false, isSaving = false, apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to update secondary parent." }, infoMessage = "") }
                                return@launch
                            }
                            runCatching { updateParentLinks(draft) }.fold(
                                onSuccess = {
                                    completeParentMutation("Parent details updated successfully.")
                                },
                                onFailure = { throwable ->
                                    _uiState.update { it.copy(isLoading = false, isSaving = false, apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to update parent relationship." }, infoMessage = "") }
                                }
                            )
                        }
                    },
                    onFailure = { throwable ->
                        _uiState.update {
                            it.copy(isLoading = false, isSaving = false, apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to update parent." }, infoMessage = "")
                        }
                        scLog("$TAG updateParent failure", throwable)
                    }
                )
            }
            return
        }

        val validationError = validateCreateDraft(draft)
        if (validationError != null) {
            _uiState.update { it.copy(errorMessage = validationError, apiErrorMessage = "", infoMessage = "") }
            return
        }

        _uiState.update { it.copy(isLoading = true, isSaving = true, errorMessage = "", apiErrorMessage = "", infoMessage = "") }
        scope.launch {
            val request = try {
                draft.toCreateRequest()
            } catch (throwable: Throwable) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to prepare the parent request." },
                        infoMessage = ""
                    )
                }
                scLog("$TAG saveParent request preparation failure", throwable)
                return@launch
            }
            ParentRepository.createParentAndLink(
                request = request,
                schoolId = normalizedSchoolId,
                currentUserId = UserSessionStore.getUserId().trim().ifBlank { null }
            ).fold(
                onSuccess = {
                    completeParentMutation("Parent added successfully.")
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to add parent." },
                            infoMessage = ""
                        )
                    }
                    scLog("$TAG saveParent failure", throwable)
                }
            )
        }
    }

    private fun completeParentMutation(message: String) {
        ParentRepository.invalidateCache(normalizedSchoolId)
        AppToastCenter.showSuccess(message)
        _uiState.update {
            it.copy(
                isLoading = false,
                isSaving = false,
                errorMessage = "",
                apiErrorMessage = "",
                infoMessage = "",
                mutationSuccessNonce = it.mutationSuccessNonce + 1
            )
        }
        refreshParentsData(forceRemote = true)
    }

    private fun validateCreateDraft(draft: ParentEditorDraft): String? {
        if (normalizedSchoolId.isBlank()) return "School context was not available."
        if (draft.selectedStudentId.isBlank()) return "Select a student."
        if (draft.primaryContactName.isBlank()) return "Enter the primary contact name."
        if (draft.primaryContactMobile.isBlank()) return "Enter the primary contact mobile number."
        val hasSecondary = listOf(
            draft.secondaryContactName,
            draft.secondaryContactMobile,
            draft.secondaryContactEmail
        ).any { it.isNotBlank() }
        if (hasSecondary && (draft.secondaryContactName.isBlank() || draft.secondaryContactMobile.isBlank())) {
            return "Enter both the secondary contact name and mobile number, or leave the secondary contact empty."
        }
        return null
    }

    private fun validateEditDraft(draft: ParentEditorDraft): String? {
        if (normalizedSchoolId.isBlank()) return "School context was not available."
        if (draft.id.isBlank()) return "Parent record was not available."
        val validation = validateParentEditor(draft)
        listOf(
            validation.primaryName,
            validation.primaryMobile,
            validation.primaryEmail,
            validation.secondaryName,
            validation.secondaryMobile,
            validation.secondaryEmail
        ).firstOrNull(String::isNotBlank)?.let { return it }
        return null
    }

    private suspend fun updateParentLinks(draft: ParentEditorDraft) {
        if (draft.primaryLinkId.isNotBlank()) {
            ParentRepository.updateParentLink(
                draft.primaryLinkId,
                StudentParentLinkUpdateRequest(
                    relationshipType = draft.primaryRelationshipCode.toApiRelationshipType(),
                    isPrimaryContact = true,
                    isActive = draft.statusCode == SchoolKeys.V_STATUS_ACTIVE
                )
            ).getOrThrow()
        }
        if (draft.secondaryLinkId.isNotBlank()) {
            ParentRepository.updateParentLink(
                draft.secondaryLinkId,
                StudentParentLinkUpdateRequest(
                    relationshipType = draft.secondaryRelationshipCode.toApiRelationshipType(),
                    isPrimaryContact = false,
                    isActive = draft.statusCode == SchoolKeys.V_STATUS_ACTIVE
                )
            ).getOrThrow()
        }
    }

    private fun refreshParentsPage(forceRemote: Boolean) {
        val currentState = _uiState.value
        if (bootstrapSequencer.shouldDeferPageReload(
                forceRemote = forceRemote,
                analyticsLoading = currentState.isAnalyticsLoading,
                hasAnalytics = currentState.analytics != null
            )
        ) {
            return
        }
        bootstrapSequencer.launchPage(forceRemote = forceRemote) { pageForceRemote ->
            loadParentPage(pageForceRemote)
        }
    }

    private fun scheduleParentsReload(immediate: Boolean = false) {
        reloadDebouncer.submit(immediate = immediate) {
            refreshParentsPage(forceRemote = false)
        }
    }

    private suspend fun loadParentAnalytics(forceRemote: Boolean) {
        val cached = SchoolStatisticsRepository.peekSchoolStatistics(normalizedSchoolId)
        if (cached == null) {
            _uiState.update { it.copy(isAnalyticsLoading = true) }
        }
        val result = SchoolStatisticsRepository.loadSchoolStatistics(
            schoolId = normalizedSchoolId,
            forceRemote = forceRemote
        )
        result.fold(
            onSuccess = { analytics ->
                _uiState.update {
                    it.copy(
                        isAnalyticsLoading = false,
                        analytics = analytics,
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
            },
            onFailure = { throwable ->
                _uiState.update {
                    it.copy(
                        isAnalyticsLoading = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load parent analytics." }
                    )
                }
            }
        )
    }

    private fun buildQuery(state: ParentsUiState): String {
        return listOf(
            state.searchQuery,
            state.filters.parentName,
            state.filters.studentQuery,
            if (state.filters.classSectionId.isBlank()) state.filters.className else "",
            if (state.filters.classSectionId.isBlank()) state.filters.section else "",
            state.filters.mobile
        ).joinToString(" ").trim()
    }

    private suspend fun loadParentPage(forceRemote: Boolean) {
        val currentState = _uiState.value
        val query = buildQuery(currentState)
        val isActive: Boolean? = null
        val filters = currentState.filters
        val selectedClassSection = currentState.filterClassSectionOptions.firstOrNull { it.id == filters.classSectionId }
        val hasPostOnlyFilter = listOf(
            filters.parentName,
            filters.studentQuery,
            filters.primaryRelationshipCode,
            filters.secondaryRelationshipCode,
            filters.mobile,
            filters.createdFrom,
            filters.createdTo
        ).any { it.isNotBlank() } || (filters.classSectionId.isBlank() && (filters.className.isNotBlank() || filters.section.isNotBlank()))
        val advancedFilter = if (hasPostOnlyFilter) ParentFilterRequest(
            parentNameQuery = filters.parentName.trim().ifBlank { null },
            studentQuery = filters.studentQuery.trim().ifBlank { null },
            classId = filters.className.ifBlank { selectedClassSection?.className.orEmpty() }.trim().ifBlank { null },
            sectionId = filters.section.ifBlank { selectedClassSection?.sectionName.orEmpty() }.trim().ifBlank { null },
            primaryRelationship = filters.primaryRelationshipCode.trim().toApiRelationshipType().ifBlank { null },
            secondaryRelationship = filters.secondaryRelationshipCode.trim().toApiRelationshipType().ifBlank { null },
            mobileQuery = filters.mobile.trim().ifBlank { null },
            createdDateFrom = appDateInputToApi(filters.createdFrom).orEmpty().ifBlank { null },
            createdDateTo = appDateInputToApi(filters.createdTo).orEmpty().ifBlank { null }
        ).takeIf { it != ParentFilterRequest() } else null
        val pageIndex = currentState.pageIndex
        val pageSize = currentState.pageSize

        _uiState.update {
            it.copy(
                isLoading = false,
                isPageLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scLog(
            "$TAG loadParentPage start school=$normalizedSchoolId q=${query.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$pageIndex pageSize=$pageSize forceRemote=$forceRemote"
        )
        val result = NetworkActivityTracker.withoutBlockingLoader {
            ParentRepository.loadSchoolParentsPage(
                schoolId = normalizedSchoolId,
                query = query,
                classSectionId = filters.classSectionId.trim().ifBlank { null },
                isActive = isActive,
                advancedFilter = advancedFilter,
                pageIndex = pageIndex,
                pageSize = pageSize,
                sort = ServerSort(
                    field = currentState.sortField.apiField,
                    order = if (currentState.isSortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                ),
                forceRemote = forceRemote
            )
        }

        result.fold(
            onSuccess = { responses ->
                val serverTotalCount = responses.serverTotalCountOrNull()
                val rows = responses.map { it.toUiRow() }
                val pageLoad = resolveServerPageLoadState(
                    exactTotal = serverTotalCount,
                    paging = ServerPagingState(
                        pageIndex = pageIndex,
                        pageSize = pageSize,
                        totalCount = serverTotalCount ?: rows.size,
                        isTotalCountExact = serverTotalCount != null
                    ),
                    currentItemCount = rows.size,
                    pageOffset = pageIndex.coerceAtLeast(0) * pageSize.coerceAtLeast(1)
                )
                _uiState.update { current ->
                    current.copy(
                        isLoading = false,
                        isPageLoading = false,
                        parents = rows,
                        pageIndex = pageIndex.coerceAtLeast(0),
                        totalCount = pageLoad.paging.totalCount,
                        isTotalCountExact = pageLoad.paging.isTotalCountExact,
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                if (
                    rows.isEmpty() &&
                    !forceRemote &&
                    advancedFilter == null && query.isBlank() &&
                    isActive == null &&
                    pageIndex == 0 &&
                    pageLoad.paging.totalCount > 0
                ) {
                    scLog(
                        "$TAG loadParentPage emptyFirstPage retryWithForceRemote school=$normalizedSchoolId listTotal=${pageLoad.paging.totalCount}"
                    )
                    val retryResult = ParentRepository.loadSchoolParentsPage(
                        schoolId = normalizedSchoolId,
                        query = query,
                        classSectionId = filters.classSectionId.trim().ifBlank { null },
                        isActive = isActive,
                        pageIndex = pageIndex,
                        pageSize = pageSize,
                        sort = ServerSort(
                            field = currentState.sortField.apiField,
                            order = if (currentState.isSortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                        ),
                        forceRemote = true
                    )
                    retryResult.getOrNull()?.let { retryRows ->
                        val mappedRetryRows = retryRows.map { it.toUiRow() }
                        _uiState.update { current ->
                            current.copy(
                                isLoading = false,
                                isPageLoading = false,
                                parents = mappedRetryRows
                            )
                        }
                    }
                }
                scLog(
                    "$TAG loadParentPage complete school=$normalizedSchoolId count=${rows.size} totalCount=${pageLoad.paging.totalCount} exact=${pageLoad.paging.isTotalCountExact}"
                )
            },
            onFailure = { throwable ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isPageLoading = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load parents." }
                    )
                }
                scLog("$TAG loadParentPage failure school=$normalizedSchoolId", throwable)
            }
        )
    }

    private fun loadStudentPickerPage(reset: Boolean, forceRemote: Boolean) {
        studentPickerJob?.cancel()
        studentPickerJob = scope.launch {
            val state = _uiState.value
            val query = state.studentPickerQuery.trim()
            if (query.isNotEmpty() && query.length < 2) {
                _uiState.update { it.copy(studentPickerIsLoading = false, studentPickerIsLoadingMore = false, studentPickerHasMore = false) }
                return@launch
            }
            val pageIndex = if (reset) 0 else state.studentPickerPageIndex + 1
            _uiState.update {
                it.copy(
                    studentPickerIsLoading = reset,
                    studentPickerIsLoadingMore = !reset,
                    studentPickerError = ""
                )
            }
            ParentRepository.loadStudentsWithParentsPage(
                schoolId = normalizedSchoolId,
                query = query,
                isActive = true,
                pageIndex = pageIndex,
                pageSize = com.schoolconnect.app.feature.common.PaginationPolicy.DEFAULT_PAGE_SIZE,
                sort = ServerSort("admission_number", ServerSortOrder.ASC),
                forceRemote = forceRemote
            ).fold(
                onSuccess = { response ->
                    val total = response.serverTotalCountOrNull() ?: (pageIndex * 20 + response.size)
                    val mapped = response.mapNotNull { student ->
                        val id = student.id?.trim().orEmpty()
                        if (id.isBlank()) null else ParentStudentOptionUi(
                            id = id,
                            admissionNo = student.admissionNumber.orEmpty(),
                            studentName = student.name.orEmpty(),
                            className = student.className.orEmpty(),
                            section = student.sectionName.orEmpty(),
                            rollNo = student.rollNumber.orEmpty(),
                            statusCode = if (student.isActive == true) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE,
                            parents = student.parents.orEmpty()
                        )
                    }
                    _uiState.update { current ->
                        val options = if (reset) mapped else (current.studentOptions + mapped).distinctBy { it.id }
                        current.copy(
                            studentOptions = options,
                            studentPickerPageIndex = pageIndex,
                            studentPickerTotalCount = total,
                            studentPickerIsLoading = false,
                            studentPickerIsLoadingMore = false,
                            studentPickerHasMore = options.size < total && mapped.isNotEmpty(),
                            studentPickerError = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            studentPickerIsLoading = false,
                            studentPickerIsLoadingMore = false,
                            studentPickerError = throwable.message.orEmpty().ifBlank { "Unable to load students." }
                        )
                    }
                    scLog("$TAG loadStudentPickerPage failure school=$normalizedSchoolId query=${query.ifBlank { "<blank>" }} pageIndex=$pageIndex", throwable)
                }
            )
        }
    }

    fun clear() {
        _uiState.update { it.copy(isLoading = false, isSaving = false, apiErrorMessage = "") }
        analyticsJob?.cancel()
        parentsJob?.cancel()
        bootstrapJob?.cancel()
        studentPickerJob?.cancel()
        classSectionLookupJob?.cancel()
        studentPickerDebouncer.cancel()
        bootstrapSequencer.clear()
        scope.cancel()
    }
}

private suspend fun ParentEditorDraft.toCreateRequest(): StudentParentWrapperCreateRequest {
    com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
        .loadCategoryItems("PARENT_RELATIONSHIP")
        .getOrElse { throw IllegalStateException("Unable to load parent relationship options.", it) }
    val secondaryProvided = listOf(
        secondaryContactName,
        secondaryContactMobile,
        secondaryContactEmail
    ).any { it.isNotBlank() }
    return StudentParentWrapperCreateRequest(
        studentId = selectedStudentId.trim(),
        primaryParent = ParentWrapperData(
            name = primaryContactName.trim(),
            mobile = primaryContactMobile.trim(),
            email = primaryContactEmail.trim().ifBlank { null },
            customAttributes = mapOf(
                "primary_contact_name" to primaryContactName.trim(),
                "primary_contact_phone" to primaryContactMobile.trim(),
                "primary_contact_email" to primaryContactEmail.trim()
            ),
            isActive = true
        ),
        secondaryParent = if (secondaryProvided) ParentWrapperData(
            name = secondaryContactName.trim(),
            mobile = secondaryContactMobile.trim(),
            email = secondaryContactEmail.trim().ifBlank { null },
            customAttributes = mapOf(
                "secondary_contact_name" to secondaryContactName.trim(),
                "secondary_contact_phone" to secondaryContactMobile.trim(),
                "secondary_contact_email" to secondaryContactEmail.trim()
            ),
            isActive = true
        ) else null,
        primaryRelationshipType = primaryRelationshipCode.toApiRelationshipType(),
        secondaryRelationshipType = secondaryRelationshipCode.toApiRelationshipType(),
        primaryIsPrimaryContact = true,
        secondaryIsPrimaryContact = false
    )
}

private fun String.toApiRelationshipType(): String {
    val normalized = trim()
    val lookupValue = when (normalized) {
        SchoolKeys.V_REL_FATHER -> "Father"
        SchoolKeys.V_REL_MOTHER -> "Mother"
        SchoolKeys.V_REL_GUARDIAN -> "Guardian"
        else -> normalized
    }
    return com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode(
        "PARENT_RELATIONSHIP",
        lookupValue
    )
}

private fun StudentListResponse.toUiRow(): ParentRowUi {
    val activeParents = parents.orEmpty().filter { it.isActive != false }
    val primary = activeParents.firstOrNull { it.isPrimaryContact == true }
        ?: activeParents.firstOrNull()
    val secondary = activeParents.firstOrNull { it !== primary }
    return ParentRowUi(
        id = primary?.parentId.orEmpty().ifBlank { id.orEmpty() },
        admissionNo = admissionNumber.orEmpty(),
        studentId = id.orEmpty(),
        studentName = name.orEmpty(),
        className = className.orEmpty(),
        section = sectionName.orEmpty(),
        rollNo = rollNumber.orEmpty(),
        linkedStudents = listOf(
            ParentLinkedStudentUi(
                studentId = id.orEmpty(),
                studentName = name.orEmpty(),
                admissionNo = admissionNumber.orEmpty(),
                className = className.orEmpty(),
                section = sectionName.orEmpty(),
                relationshipType = primary?.relationshipType.orEmpty(),
                isPrimaryContact = primary?.isPrimaryContact == true
            )
        ),
        primaryContactName = primary?.name.orEmpty(),
        primaryContactMobile = primary?.mobile.orEmpty(),
        primaryContactEmail = primary?.email.orEmpty(),
        primaryRelationshipCode = primary?.relationshipType.orEmpty(),
        secondaryContactName = secondary?.name.orEmpty(),
        secondaryContactMobile = secondary?.mobile.orEmpty(),
        secondaryContactEmail = secondary?.email.orEmpty(),
        secondaryRelationshipCode = secondary?.relationshipType.orEmpty(),
        statusCode = if (primary?.isActive == true && isActive != false) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE,
        createdBy = createdByName.orEmpty().ifBlank { createdById.orEmpty() }.ifBlank { "-" },
        createdAtText = formatApiTimestamp(createdAt.orEmpty()),
        updatedBy = updatedByName.orEmpty().ifBlank { updatedById.orEmpty() }.ifBlank { "-" },
        updatedAtText = formatApiTimestamp(updatedAt.orEmpty()),
        createdAtMillis = apiTimestampSortKey(createdAt.orEmpty()),
        updatedAtMillis = apiTimestampSortKey(updatedAt.orEmpty())
        ,primaryLinkId = primary?.linkId.orEmpty()
        ,primaryLinkIsActive = primary?.isActive == true
        ,secondaryParentId = secondary?.parentId.orEmpty()
        ,secondaryLinkId = secondary?.linkId.orEmpty()
    )
}

private fun formatApiTimestamp(value: String): String {
    val normalized = value.trim()
    if (normalized.isBlank()) return "-"

    val datePart = normalized.substringBefore('T').takeIf { it.length >= 10 }?.take(10)
        ?: return normalized
    val dateTokens = datePart.split("-")
    if (dateTokens.size != 3) return normalized

    val year = dateTokens[0]
    val month = dateTokens[1].toIntOrNull()?.let { monthName(it) } ?: return normalized
    val day = dateTokens[2].trimStart('0').ifBlank { "0" }

    return "$day $month $year"
}

private fun monthName(month: Int): String = when (month) {
    1 -> "Jan"
    2 -> "Feb"
    3 -> "Mar"
    4 -> "Apr"
    5 -> "May"
    6 -> "Jun"
    7 -> "Jul"
    8 -> "Aug"
    9 -> "Sep"
    10 -> "Oct"
    11 -> "Nov"
    12 -> "Dec"
    else -> "Jan"
}

private fun apiTimestampSortKey(value: String): Long {
    val normalized = value.trim()
    if (normalized.isBlank()) return 0L
    val millis = normalized.substringBefore('.').replace("T", " ").replace("Z", "").trim()
    val parts = millis.split(Regex("\\s+"))
    if (parts.size < 2) return 0L
    val dateParts = parts[0].split("-")
    val timeParts = parts[1].split(":")
    if (dateParts.size != 3 || timeParts.size < 3) return 0L
    val year = dateParts[0].toLongOrNull() ?: return 0L
    val month = dateParts[1].toLongOrNull() ?: return 0L
    val day = dateParts[2].toLongOrNull() ?: return 0L
    val hour = timeParts[0].toLongOrNull() ?: return 0L
    val minute = timeParts[1].toLongOrNull() ?: return 0L
    val second = timeParts[2].toLongOrNull() ?: return 0L
    return (((((year * 100 + month) * 100 + day) * 100 + hour) * 100 + minute) * 100) + second
}
